import PolicyLayout from "../components/PolicyLayout";

const TermsPage = () => {
  return (
    <PolicyLayout title="Terms of Service" lastUpdated="February 2026">
      <p className="text-base leading-relaxed mb-6">
        These Terms of Service ("Terms") govern your access to and use of the
        website and services provided by{" "}
        <strong>theSnugglePrints</strong> ("we", "us", "our"). By using our
        website or placing an order, you agree to be bound by these Terms.
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">1. Services</h3>
      <p className="text-base leading-relaxed mb-6">
        We offer AI-generated pet portraits, photo books, and related
        personalised products. You upload a photo of your pet (and optionally a
        description or storyline), we generate a custom artwork or photo-book
        design, and upon your approval and payment, we print and ship the
        product to the address you provide. We ship to customers within India
        and to a growing list of international destinations.
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">2. User Accounts &amp; Eligibility</h3>
      <p className="text-base leading-relaxed mb-6">
        You must be at least 18 years of age to place an order. By using our
        service, you represent that the information you provide is accurate
        and that you have the right to upload any images you submit.
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">3. Orders &amp; Payment</h3>
      <ul className="list-disc ml-6 space-y-2 mb-6">
        <li>
          Prices are shown in <strong>INR (₹) for customers in India</strong>{" "}
          and in <strong>USD ($) for international customers</strong>, based
          on the country we detect from your location. Prices include
          applicable taxes unless noted.
        </li>
        <li>
          Payments are currently processed via <strong>Razorpay (INR)</strong>{" "}
          for all customers, including international orders.{" "}
          <strong>PayPal-based USD checkout is launching soon</strong> for
          international customers.
        </li>
        <li>
          Indian GST is included for orders shipped within India. For
          international orders, the product may be subject to local VAT, sales
          tax, or import duties on delivery — see our{" "}
          <a href="/shipping-policy" className="underline font-medium">
            Shipping Policy
          </a>{" "}
          for how we handle these.
        </li>
        <li>
          Orders are accepted only after successful payment. We reserve the
          right to refuse or cancel any order at our sole discretion (e.g.,
          suspected fraud, payment failure, or inappropriate content).
        </li>
        <li>
          You will receive an order confirmation email with a reference number
          (e.g., <em>PET-YYYYMMDD-XXXXXX</em>).
        </li>
      </ul>

      <h3 className="text-2xl font-bold mt-10 mb-4">4. Intellectual Property</h3>
      <p className="text-base leading-relaxed mb-6">
        You retain full ownership of the original photos and descriptions you
        upload. By uploading, you grant us a non-exclusive, royalty-free
        licence to use your image and content solely to generate, print, and
        deliver your custom art. The final artwork we generate is licensed to
        you for personal, non-commercial use. All trademarks, logos, and
        website content are owned by theSnugglePrints.
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">5. Acceptable Use</h3>
      <p className="text-base leading-relaxed mb-4">You agree NOT to:</p>
      <ul className="list-disc ml-6 space-y-2 mb-6">
        <li>Upload images you do not own or have rights to</li>
        <li>Upload images containing minors in an inappropriate context, nudity, violence, or hate content</li>
        <li>Use our service for any unlawful or fraudulent purpose</li>
        <li>Attempt to reverse-engineer, scrape, or overload our systems</li>
      </ul>

      <h3 className="text-2xl font-bold mt-10 mb-4">6. AI-Generated Content</h3>
      <p className="text-base leading-relaxed mb-6">
        Our AI (Google Gemini — Nano Banana) produces original illustrations
        based on your uploaded reference. While we aim for strong likeness, AI
        output is probabilistic — minor variations from the reference are
        normal and expected. You will always preview the generated artwork
        before payment, and may regenerate it as part of the same session if
        not satisfied. Once payment is made, the chosen artwork is considered
        approved for print. For Photo Books, we additionally share a draft
        with you for review before sending the final file to print.
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">7. Shipping &amp; Delivery</h3>
      <p className="text-base leading-relaxed mb-6">
        Please review our{" "}
        <a href="/shipping-policy" className="underline font-medium">
          Shipping Policy
        </a>{" "}
        for timelines, country coverage, and how we handle customs duties on
        international orders.
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">8. Refunds &amp; Cancellation</h3>
      <p className="text-base leading-relaxed mb-6">
        Because every product is personalised and you approve a preview before
        paying, orders cannot be cancelled once payment is made. Eligible
        defective or damaged products are replaced or refunded as described in
        our{" "}
        <a href="/refund-policy" className="underline font-medium">
          Refund &amp; Cancellation Policy
        </a>
        .
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">9. Limitation of Liability</h3>
      <p className="text-base leading-relaxed mb-6">
        To the maximum extent permitted by law, our liability for any claim
        arising from your use of the service is limited to the amount you paid
        for the relevant order. We are not liable for indirect, incidental, or
        consequential damages.
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">10. Indemnification</h3>
      <p className="text-base leading-relaxed mb-6">
        You agree to indemnify and hold us harmless from any claim arising
        from your breach of these Terms or your misuse of the service
        (including uploading content you don't own).
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">11. Governing Law &amp; Jurisdiction</h3>
      <p className="text-base leading-relaxed mb-6">
        These Terms are governed by the laws of India. Any disputes arising
        out of or in connection with these Terms or your use of our service —
        whether you are based in India or internationally — will be subject to
        the exclusive jurisdiction of the courts of Bangalore, Karnataka,
        India.
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">13. Instagram Messaging Service</h3>
      <p className="text-base leading-relaxed mb-4">
        theSnugglePrints operates an Instagram business account (
        <strong>@thesnuggleprints</strong>) and uses the{" "}
        <strong>Meta Messenger Platform API</strong> to receive and respond to
        Instagram Direct Messages (DMs) sent to that account. By sending us a
        DM on Instagram, you agree to the following terms regarding how those
        messages are handled.
      </p>
      <ul className="list-disc ml-6 space-y-2 mb-6">
        <li>
          <strong>What triggers a response:</strong> When you send a DM to
          @thesnuggleprints, our system receives your Instagram username, your
          account ID, and the text of your message via the Meta Messenger
          Platform webhook.
        </li>
        <li>
          <strong>Automated AI replies:</strong> Your message is processed by
          an AI assistant (powered by Anthropic Claude via the Emergent LLM
          platform) which generates a reply based solely on the business
          information and instructions we have configured. The AI does not
          have access to your account beyond what you share in the
          conversation.
        </li>
        <li>
          <strong>Human review:</strong> Our team can review all DM
          conversations at any time in our internal customer management
          system. Certain trigger words (e.g. "refund", "complaint") cause
          the AI to pause automatically and flag the thread for a human to
          respond.
        </li>
        <li>
          <strong>Reply format:</strong> Automated replies may be sent as
          multiple short messages in sequence to simulate a natural
          conversation. There is no charge to you for receiving these
          messages.
        </li>
        <li>
          <strong>Opt-out:</strong> You can stop receiving automated replies
          at any time by replying <em>"STOP"</em>, by blocking our account,
          or by emailing{" "}
          <a href="mailto:contact@thesnuggleprints.com" className="underline font-medium">
            contact@thesnuggleprints.com
          </a>{" "}
          and requesting to be removed.
        </li>
      </ul>

      <h3 className="text-2xl font-bold mt-10 mb-4">14. Data Collected via Instagram DMs</h3>
      <p className="text-base leading-relaxed mb-4">
        When you message us on Instagram, we store the following data in our
        secure database (hosted on MongoDB Atlas):
      </p>
      <ul className="list-disc ml-6 space-y-2 mb-6">
        <li>Your Instagram username and account ID</li>
        <li>The full text of messages you send to us and our replies</li>
        <li>
          Any additional information you voluntarily share (e.g. your pet's
          name, your email address, a shipping address for an order)
        </li>
        <li>Timestamps of each message</li>
      </ul>
      <p className="text-base leading-relaxed mb-4">
        <strong>We do not:</strong>
      </p>
      <ul className="list-disc ml-6 space-y-2 mb-6">
        <li>Access your Instagram profile, photos, followers, or any data beyond what you directly send us</li>
        <li>Send unsolicited DMs or marketing messages via Instagram</li>
        <li>Sell, rent, or share your message data with any third party for advertising or marketing purposes</li>
        <li>Store your Instagram password or any authentication credentials</li>
      </ul>
      <p className="text-base leading-relaxed mb-6">
        <strong>Retention:</strong> Instagram message data is retained for a
        maximum of <strong>90 days</strong> from the last message, after
        which it is automatically deleted. If your conversation is linked to
        a confirmed order, associated order records are retained for up to 7
        years as required by Indian tax law.
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">15. AI Processing of Messages</h3>
      <p className="text-base leading-relaxed mb-6">
        To generate automated replies, the text of your most recent message
        and a limited history of the current conversation (up to 20 messages)
        are sent to the <strong>Anthropic Claude API</strong> via the Emergent
        LLM platform. Anthropic processes this data in accordance with their{" "}
        <a
          href="https://www.anthropic.com/legal/privacy"
          target="_blank"
          rel="noreferrer"
          className="underline font-medium"
        >
          Privacy Policy
        </a>
        . We do not use your conversation data to train any AI model. The AI
        is given only the business instructions we configure and the
        conversation context — it has no access to any of your personal
        accounts or external data.
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">16. Meta Platform Compliance</h3>
      <p className="text-base leading-relaxed mb-6">
        Our use of the Instagram and Messenger APIs is governed by the{" "}
        <a
          href="https://developers.facebook.com/terms/"
          target="_blank"
          rel="noreferrer"
          className="underline font-medium"
        >
          Meta Platform Terms
        </a>{" "}
        and the{" "}
        <a
          href="https://www.facebook.com/policies/ads/"
          target="_blank"
          rel="noreferrer"
          className="underline font-medium"
        >
          Meta Developer Policies
        </a>
        . We only request and use the following permissions:
      </p>
      <ul className="list-disc ml-6 space-y-2 mb-6">
        <li><strong>instagram_basic</strong> — to identify the Instagram account linked to our Facebook Page</li>
        <li><strong>instagram_manage_messages</strong> — to receive and send Instagram Direct Messages for customer support</li>
        <li><strong>pages_show_list</strong> — to identify our connected Facebook Page</li>
        <li><strong>pages_read_engagement</strong> — to read Page information</li>
        <li><strong>pages_manage_metadata</strong> — to manage our Page's webhook subscriptions</li>
      </ul>
      <p className="text-base leading-relaxed mb-6">
        These permissions are used solely for the purpose of responding to
        customer enquiries via Instagram DM. We do not use them for any other
        purpose.
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">17. Data Deletion — Instagram</h3>
      <p className="text-base leading-relaxed mb-6">
        To request deletion of all data associated with your Instagram
        account, please follow the instructions on our{" "}
        <a href="/data-deletion.html" className="underline font-medium">
          Data Deletion page
        </a>
        . Requests are processed within 14 days. You can also revoke our
        app's access to your account at any time by going to your Instagram
        Settings → Security → Apps and Websites and removing theSnugglePrints.
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">18. Changes to Terms</h3>
      <p className="text-base leading-relaxed mb-6">
        We may revise these Terms at any time by posting the updated version
        on this page. Your continued use of the service constitutes acceptance
        of the revised Terms.
      </p>

      <h3 className="text-2xl font-bold mt-10 mb-4">19. Contact</h3>
      <p className="text-base leading-relaxed mb-6">
        Questions? Email <strong>contact@thesnuggleprints.com</strong>.
      </p>
    </PolicyLayout>
  );
};

export default TermsPage;
